import React, { useCallback, useMemo, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Box, Grid, Stack, Typography } from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';


import BlockContainer from "../../components/Container/BlockContainer";
import MainContainer from "../../components/Container/MainContainer";
import ConciergeEditItem from "../../components/ConciergeEditItem";
import BaseLink from "../../components/BaseLink";
import ConciergeItemSkeleton from "../../components-skeleton/ConciergeItemSkeleton";

import { refetchMe } from "../../store";
import { useLazyQuery, useMutation } from '@apollo/client';

import { AddStaffMemberOfUser, RemoveStaffMemberOfUser, GetStaffUsers } from "../../apollo-client/api-query";
import BaseButton from "../../components/BaseButton";
import BaseListBox from "../../components/BaseListBox";
import useAuthWithRoles from "../../hooks/use-auth";
import { findMemberByUserId } from "../../utils/member-lookup";

const EditStaff = () => {
  const [getStaffUsers, { loading: loadingFetch }] = useLazyQuery(GetStaffUsers, {
    fetchPolicy: 'no-cache',
  });
  const [staffmemberoptions, setStaffMemberOptions] = useState([]);

  const [staffroleoptions, setStaffRoleOptions] = useState([]);

  const [selectedStaffMemberOption, setSelectedStaffMemberOption] = useState(null);

  const handleStaffMemberChange = (option) => {
    setSelectedStaffMemberOption(option);
  };

  const [selectedStaffRoleOption, setSelectedStaffRoleOption] = useState(null);

  const handleStaffRoleChange = (option) => {
    setSelectedStaffRoleOption(option);
  };

  const dispatch = useDispatch()
  const { id } = useParams()
  const members = useSelector(state => state.members)
  const loadingAuth = useSelector(state => state.loadingAuth)
  const selectedPatient = useMemo(() => findMemberByUserId(members, id), [members, id])
  const [memberItems, setMemberItems] = useState(selectedPatient?.user?.assignedStaff || [])
  const [addStaffMember, setAddStaffMember] = useState(false)
  const [activeAddStaff, setActiveAddStaff] = useState(false)
  const [addStaffMemberOfUser] = useMutation(AddStaffMemberOfUser)
  const [removeStaffMemberOfUser] = useMutation(RemoveStaffMemberOfUser)
  const [init_data, setInitData] = useState([])

  const { user } = useAuthWithRoles();

  const fetchData = useCallback(async (first) => {
    if (!loadingFetch) {
      const result = await getStaffUsers({
        variables: {
          first,
          roles: ['COUNSELOR', 'SUPPORT', 'PHYSICIAN', 'DIETICIAN', 'PHARMACIST', 'STAFF_PHYSICIAN'],
        }
      });
      if (result?.data?.users?.edges?.length) {
        const staffArray = result.data.users.edges.map(edge => ({
          staffId: edge.node.id,
          staffName: edge.node.name,
          staffRole: edge.node.roles,
          validStaffRoles: edge.node.validStaffRoles,
        }))
        .sort((a, b) => a.staffName.localeCompare(b.staffName));
        setInitData(staffArray);
      }
    }
  }, [getStaffUsers, loadingFetch]);

  useEffect(() => {
    fetchData(400);
  }, []);

  const makeTitlecase = (text) => {
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  }

  useEffect(() => {
    setMemberItems(selectedPatient?.user?.assignedStaff || [])
  }, [selectedPatient])

  useEffect(() => {
    const staffMemberArray = init_data
      .filter(initItem => !memberItems.some(member => initItem.staffId === member.id))
      .map(initItem => ({
        value: initItem.staffId,
        label: initItem.staffName,
      }));

    setStaffMemberOptions(staffMemberArray);
    //dispatch(refetchMe(true))
  }, [memberItems, user])

  useEffect(() => {
    const staffroleArray = [];
    const selectedRole = [];
    let availableRoles = [];

    // Extract selected roles for the staff member
    memberItems.forEach(item => {
      if (item.staffId === selectedStaffMemberOption?.value) {
        selectedRole.push(item.role.toUpperCase());
      }
    });

    // Find available roles for the staff member
    availableRoles = init_data.find(
      data => data.staffId === selectedStaffMemberOption?.value
    )?.validStaffRoles || [];

    // Filter roles that are not already selected
    availableRoles.forEach(role => {
      if (!selectedRole.includes(role)) {
        staffroleArray.push({
          value: role,
          label: makeTitlecase(role),
        });
      }
    });

    setStaffRoleOptions(staffroleArray);
    setSelectedStaffRoleOption(null);
  }, [selectedStaffMemberOption])

  useEffect(() => {
    setActiveAddStaff(selectedStaffMemberOption && selectedStaffRoleOption)
  }, [selectedStaffRoleOption])

  const removeStaffMemberItem = async (indexToRemove) => {
    const { data } = await removeStaffMemberOfUser({
      variables: {
        id: memberItems[indexToRemove]?.id
      }
    });
    if (data?.delete === true) {
      setMemberItems((prevItems) => prevItems.filter((_, index) => index !== indexToRemove));
      setSelectedStaffMemberOption(null);
      setSelectedStaffRoleOption(null);
      dispatch(refetchMe(true))
    }
  }

  const handleClickAddStaffMemeber = async (item) => {
    if (!activeAddStaff) return;
    try {
      const { data } = await addStaffMemberOfUser({
        variables: {
          userStaffInput: {
            role: item.staff_role,
            memberId: id,
            staffId: item.staff_id,
            note: "Member-viewable note",
            staffNote: "Staff-viewable note"
          }
        }
      });

      setMemberItems(data?.assignUserStaff?.member?.assignedStaff || []);
      setSelectedStaffMemberOption(null);
      setSelectedStaffRoleOption(null);
      dispatch(refetchMe(true))
    } catch (e) {
      console.debug(e)
    }
  }

  return (
    <MainContainer>
      <BlockContainer
        sx={{
          position: 'relative'
        }}
      >
        <Box
          sx={{
            width: "100%",
            padding: { xs: '24px 16px', md: '32px 16px 0' },
            borderRadius: { md: '16px' },
            boxShadow: '0px 2px 10px 0px #5B87EF1A',
            bgcolor: 'white'
          }}
        >
          <Stack spacing={'40px'}>
            <Box>
              <BaseLink
                to={`/patient/${id}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <ArrowBackIcon
                  sx={{
                    color: 'primary.mediumGray',
                    mr: '10px'
                  }}
                />
                <Typography variant="body" color="primary.darkGray">
                  Back to homepage
                </Typography>
              </BaseLink>
            </Box>
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  gap: {
                    xs: '4px',
                    md: 2
                  },
                  alignItems: {
                    xs: 'flex-start',
                    md: 'center'
                  },
                  flexDirection: {
                    xs: 'column',
                    md: 'row'
                  }
                }}
              >
                <Typography
                  variant="h1"
                  color='primary.black'
                >
                  Edit Staff
                </Typography>
              </Box>
            </Box>
            <Box>
              <Stack spacing={{ xs: '24px', md: '16px' }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between'
                  }}
                >
                  <Typography
                    variant="h2"
                    color="primary.darkGray"
                  >
                    Details
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    padding: { md: '16px' },
                    border: '1px solid #f6f9fc',
                    borderRadius: '8px'
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between'
                    }}
                  >
                    <Typography
                      variant="h3"
                      color="primary.darkGray"
                    >
                      Staff assigned
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      borderTop: '1px solid #ABB7C0',
                      mt: '16px',
                    }}
                  >
                    {loadingAuth && (
                      <Grid container spacing={'16px'}>
                        {new Array(3).fill(null).map(((_, idx) => (
                          <Grid item xs={12} md={12} key={idx}>
                            <ConciergeItemSkeleton />
                          </Grid>
                        )))}
                      </Grid>
                    )}
                    <Grid container spacing={'16px'}>
                      {memberItems && memberItems?.map(((item, idx) => (
                        <Grid item xs={12} md={12} key={idx} display={'flex'}>
                          <ConciergeEditItem
                            item={item}
                            patient_name={selectedPatient?.user?.name}
                            onConfirm={() => removeStaffMemberItem(idx)}
                          />
                        </Grid>
                      )))}
                    </Grid>
                  </Box>
                  {!loadingAuth && (memberItems?.length === 0) && (
                    <Typography variant="body" mt="16px">Patient has no Staff assigned</Typography>
                  )}
                </Box>
                {!addStaffMember ? (
                  <Box
                    onClick={() => setAddStaffMember(true)}
                    sx={{
                      height: '100%',
                      borderRadius: '16px',
                      padding: '16px',
                      boxShadow: '0px 2px 10px 0px #5B87EF33',
                      mt: '16px',
                      textAlign: 'center',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <AddIcon
                      sx={{
                        color: 'primary.mediumGray',
                        mr: '5px'
                      }}
                    />
                    <Typography variant="h3">Add new staff member</Typography>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      height: '100%',
                      borderRadius: '16px',
                      padding: '16px',
                      boxShadow: '0px 2px 10px 0px #5B87EF33',
                      mt: '16px',
                      textAlign: 'center',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        height: '100%',
                        width: '100%',
                        borderRadius: '16px',
                        padding: '16px',
                        mt: '16px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center'
                      }}
                    >
                      <Typography variant="h3" whiteSpace={'nowrap'}>Add new staff member</Typography>
                      <Box
                        sx={{
                          width: '100%',
                          height: '1px',
                        }}
                      />
                      <CloseIcon
                        sx={{
                          color: 'neturals.black',
                          mr: '10px'
                        }}
                        onClick={() => setAddStaffMember(false)}
                      />
                    </Box>
                    <Box
                      sx={{
                        width: '100%',
                        borderRadius: '16px',
                        padding: '16px',
                        mt: '16px',
                        textAlign: 'center',
                        display: { xs: 'column', md: 'flex' },
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '30px',
                        boxSizing: 'border-box',
                      }}>
                      <Box
                        sx={{
                          width: { xs: '100%', md: '30%' },
                          boxSizing: 'border-box',
                        }}>
                        <BaseListBox
                          options={staffmemberoptions}
                          value={selectedStaffMemberOption}
                          title={'Name'}
                          placeholder="Select Staff Member"
                          isSelected={selectedStaffMemberOption}
                          onChange={(option) => handleStaffMemberChange(option)}
                        />
                      </Box>
                      <Box
                        sx={{
                          width: { xs: '100%', md: '30%' },
                        }}>
                        <BaseListBox
                          options={staffroleoptions}
                          value={selectedStaffRoleOption}
                          title={'Role'}
                          isSelected={selectedStaffRoleOption}
                          placeholder="Select Role"
                          onChange={(option) => handleStaffRoleChange(option)}
                        />
                      </Box>
                      <Box
                        sx={{
                          padding: '16px 23px 0px 16px',
                          width: { xs: '100%', md: '30%' },
                        }}>
                        <BaseButton
                          buttonType={'primary'}
                          disabled={!activeAddStaff}
                          sx={{
                            border: '0px solid',
                            borderRadius: '100px',
                            display: 'flex',
                            alignItems: 'center',
                            textAlign: 'center',
                            height: '43px',
                            width: '100%'
                          }}
                          onClick={() => handleClickAddStaffMemeber({ staff_id: selectedStaffMemberOption?.value, staff_role: selectedStaffRoleOption?.value })}
                        >
                          <Typography
                            variant="h4"
                            sx={{
                              color: 'primary.white',
                              width: '100%',
                            }}
                          >
                            Add Staff Member
                          </Typography>
                        </BaseButton>
                      </Box>
                    </Box>
                  </Box>
                )}
              </Stack>
            </Box>
          </Stack>
        </Box>
      </BlockContainer>
    </MainContainer>
  );
};

export default EditStaff;
